import React from "react";
import { v1 as uuidv1 } from "uuid";
import { Button, Card } from "@cmsgov/ds-medicare-gov";
import { useTranslate, sendTealiumEvent, $$ } from "../../../helpers";
import { MedigapPlanPolicy } from "../utils/plans";
import planDataUtils from "../../../helpers/planDataUtils";
import { useFlags } from "launchdarkly-react-client-sdk";
import { getMedigapPriceRange } from "../utils/medigapUtilities";

const MedigapPolicyCard: React.FunctionComponent<{
  policy: MedigapPlanPolicy;
}> = ({ policy }) => {
  const t = useTranslate();
  const cardId = uuidv1();
  const { mctCurrentPlanYear, tmpFeEnableMedigapPolicyPricing } = useFlags();
  const partBPremium = planDataUtils.getPartBPremiumForYear(
    mctCurrentPlanYear || new Date().getFullYear().toString()
  );

  const focusOnDemographicComponent = (): void => {
    const focusElement = document.querySelector(
      ".AccuratePriceContainer"
    ) as HTMLInputElement;
    focusElement.focus();
  };

  return (
    <Card className="MedigapPolicyCard e2e-medigap-policy-card">
      <section className="MedigapPolicyCard__header">
        <h2 className="e2e-policy-company" id={cardId}>
          {t(policy.company)}
        </h2>
      </section>
      <div className="MedigapPolicyCard__body">
        {tmpFeEnableMedigapPolicyPricing && (
          <>
            <section className="MedigapPolicyCard__monthly_cost">
              <h3 className="MedigapPolicyCard__section_heading ds-u-margin-bottom--0">
                {t("medigap.card.section_heading.monthly_cost")}
              </h3>

              <>
                <span className="ds-u-font-size--title e2e-monthly-cost">
                  {getMedigapPriceRange(
                    policy.monthly_rate_min,
                    policy.monthly_rate_max
                  )}
                </span>
              </>
              <p>
                {t("medigap.policies.cost_estimate")}{" "}
                {t("medigap.policies.contact_company_for_quote")}
              </p>
              <p>
                {t("plan_card.does_not_include")}:
                <br />
                {$$(partBPremium)} {t("plan_terms.partb_premium")}
              </p>
              <div className="ds-u-margin-top--3 mct-hide-print">
                <Button
                  variation="transparent"
                  className="ds-u-padding--0 ds-u-text-align--left"
                  onClick={focusOnDemographicComponent}
                >
                  {t("medigap.get_more_accurate_price")}
                </Button>
              </div>
            </section>
            <div className="MedigapPolicyCard__divider"></div>
          </>
        )}

        <section className="MedigapPolicyCard__contact_info">
          <h3 className="MedigapPolicyCard__section_heading">
            {t("medigap.policies.contact_company")}
          </h3>
          <div className="MedigapPolicyCard__company_field">
            <h5>{t("medigap.address")}</h5>
            {policy.address}
          </div>
          <div className="MedigapPolicyCard__company_field">
            <h5 className="ds-u-margin-bottom--0">
              {t("medigap.phone_number")}:
            </h5>
            <a
              aria-describedby={cardId}
              href={`tel:${policy.phone_number}`}
              onClick={(): void => {
                sendTealiumEvent(
                  "Step 2: Pick your policy",
                  `Result: Phone ${policy.company}`
                );
              }}
            >
              {policy.phone_number}
            </a>
          </div>
          <div className="MedigapPolicyCard__company_field">
            <h5 className="ds-u-margin-bottom--0">{t("medigap.website")}:</h5>
            <a
              aria-describedby={cardId}
              href={policy.website}
              className="mct-hide-print"
              target="_blank"
              rel="noopener noreferrer"
              onClick={(): void => {
                sendTealiumEvent(
                  "Step 2: Pick your policy",
                  `Result: Website ${policy.company}`
                );
              }}
            >
              {t("medigap.policies.visit_company_website")}
            </a>
            <span className="mct-print-only">{policy.website}</span>
          </div>
        </section>
        <div className="MedigapPolicyCard__divider"></div>
        <section className="MedigapPolicyCard__pricing_info">
          <h3 className="MedigapPolicyCard__section_heading e2e-policy-rate-type">
            {t(`medigap.rate_type.heading.${policy.rate_type.toLowerCase()}`)}
          </h3>
          <p>
            {t(
              `medigap.rate_type.description.${policy.rate_type.toLowerCase()}`
            )}
          </p>
          <p>
            <a
              aria-describedby={cardId}
              href="https://medicare.gov/supplements-other-insurance/whats-medicare-supplement-insurance-medigap/medigap-costs/costs-of-medigap-policies"
              className="mct-hide-print"
              target="_blank"
              rel="noopener noreferrer"
              onClick={(): void => {
                sendTealiumEvent(
                  "Step 2: Pick your policy",
                  "Learn More: Learn about costs"
                );
              }}
            >
              {t("medigap.policies.learn_about_costs")}
            </a>
          </p>
        </section>
      </div>
    </Card>
  );
};

export default MedigapPolicyCard;
